import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Alert, Box, Button, CircularProgress, Paper, Stack, TextField, Typography } from "@mui/material";
import React, { useRef, useState } from "react";
import { useAuthProvider, useNotify, useRedirect, useTranslate } from "react-admin";

import { captureException } from "../lib/errorHandling";
import { borderRadius, color, shadows, space } from "../theme/designTokens";

// Utility function to check password strength
const isStrongPassword = (password: string): boolean => {
  const minLength = 16;
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /\d/.test(password);
  const hasSpecialChar = /[@$!%*?&]/.test(password);
  return password.length >= minLength && hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar;
};

export const ChangePasswordScreen: React.FC = () => {
  const [oldPassword, setOldPassword] = useState<string>("");
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submissionInProgress = useRef(false);
  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();
  const authProvider = useAuthProvider();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submissionInProgress.current) return;
    setError(null);

    if (newPassword !== confirmPassword) {
      setError(translate("changePassword.passwordsDoNotMatch"));
      return;
    }

    if (!isStrongPassword(newPassword)) {
      setError(translate("changePassword.passwordRequirements"));
      return;
    }

    submissionInProgress.current = true;
    setIsSubmitting(true);
    try {
      await authProvider?.changePassword({ oldPassword, newPassword });
      notify("changePassword.success", { type: "success" });
      redirect("/account");
    } catch (caughtError) {
      captureException(caughtError, "auth/change-password: update password");
      const messageKey =
        caughtError instanceof Error && caughtError.message === "Old password is incorrect"
          ? "changePassword.oldPasswordIncorrect"
          : "changePassword.error";
      notify(messageKey, { type: "error" });
    } finally {
      submissionInProgress.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Paper
      component="form"
      onSubmit={handleSubmit}
      variant="outlined"
      sx={{
        borderColor: color.panelBorder,
        borderRadius: borderRadius.panel,
        boxShadow: shadows.card,
        maxWidth: 640,
        p: space.sm2,
      }}
    >
      <Stack gap={space.sm}>
        <Box>
          <Typography variant="h6">{translate("changePassword.title")}</Typography>
          <Typography color="text.secondary">{translate("changePassword.description")}</Typography>
        </Box>
        <TextField
          data-testid="current-password-field"
          label={translate("changePassword.oldPassword")}
          type="password"
          autoComplete="current-password"
          fullWidth
          value={oldPassword}
          onChange={e => setOldPassword(e.target.value)}
          disabled={isSubmitting}
          required
        />
        <TextField
          data-testid="new-password-field"
          label={translate("changePassword.newPassword")}
          type="password"
          autoComplete="new-password"
          fullWidth
          value={newPassword}
          onChange={e => setNewPassword(e.target.value)}
          disabled={isSubmitting}
          required
        />
        <TextField
          data-testid="confirm-password-field"
          label={translate("changePassword.confirmPassword")}
          type="password"
          autoComplete="new-password"
          fullWidth
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          disabled={isSubmitting}
          required
        />
        {error && <Alert severity="error">{error}</Alert>}
        <Stack direction="row" justifyContent="flex-end" gap={space.xs}>
          <Button type="button" variant="outlined" onClick={() => redirect("/account")} disabled={isSubmitting}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            data-testid="change-password-submit"
            type="submit"
            variant="contained"
            startIcon={isSubmitting ? <CircularProgress color="inherit" size={18} /> : <LockOutlinedIcon />}
            disabled={isSubmitting}
          >
            {translate(isSubmitting ? "changePassword.submitting" : "changePassword.submit")}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
};
